import React, { useEffect, useState } from "react";
import sha256 from "crypto-js/sha256";
import { useTranslation } from "react-i18next";
import { useAuth } from "utils/useAuth/useAuth";
import { useSnackbar } from "notistack";
import {
  isNullish,
  isEmptyOrBlank,
  agentTimezoneMapping,
  validateStrongPassword,
} from "utils/utils";
import { createAgent, getRoleList } from "./model";
import { emailFormatValidator, isAllInputValid } from "utils/formValidation";
import s from "./AddAgent.module.scss";
import Selection from "components/Input/Selection/Selection";

import Spin from "components/Spin/Spin";
import Grid from "components/Grid/Grid";
import OutlinedText from "components/Input/OutlinedText/OutlinedText";
import Divider from "components/Divider/Divider";

import EnabledOptions from "utils/commonOptions/EnabledOptions";
import AgentTimezoneOptions from "utils/AgentStatusOptions/AgentTimezoneOptions";
import AgentRoleOptions from "utils/AgentStatusOptions/AgentRoleOptions";
import AgentPermissionOptions from "utils/AgentStatusOptions/AgentPermissionOptions";
import AgentStatusOptions from "utils/AgentStatusOptions/AgentStatusOptions";
import responseCodeMapping from "utils/response_code_mapping.json";

import merchantManagement from "routes/merchantManagement";
import StrongPasswordPopper from "components/StrongPasswordPopper/StrongPasswordPopper";

const PAGE_LOCALE_KEY = "page.agents.agents_detail.";
const DIALOG_LOCALE_KEY = "page.dialogs.";

const AGENTDETAIL_FIELD_INIT_STATE = {
  isoAgentId: "",
  firstName: "",
  lastName: "",
  password: "",
  confirm: "",
  email: "",
  role: "AGENT",
  isOTPenable: "0",
  timezone: "Etc/GMT+8",
  permission: ["PORTAL"],
  status: "ACTIVE",
  roleId: "",
};

const ERROR_INIT_STATE = {
  isoAgentId: false,
  firstName: false,
  lastName: false,
  password: false,
  confirm: false,
  email: false,
  role: false,
  isOTPenable: false,
  timezone: false,
  permission: false,
  status: false,
  roleId: false,
};

const AddAgent = ({ path, redirectFn }) => {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const gridId = "AgentMerchantsListGrid";

  const [roleListLoading, setRoleListLoading] = useState(true);

  const [roleList, setRoleList] = useState([]);
  const [agentDetailFieldInput, setAgentDetailFieldInput] = useState(
    AGENTDETAIL_FIELD_INIT_STATE,
  );

  const [focus, setFocus] = useState({});
  const [error, setError] = useState(ERROR_INIT_STATE);
  const [anchorEl, setAnchorEl] = useState(null);

  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    initialFetchRolePermissionList();
  }, []);

  const initialFetchRolePermissionList = async () => {
    setRoleListLoading(true);
    const payload = {
      page: 0,
      size: 40,
    };
    const apiResponse = await getRoleList(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode === "0000") {
            const list = responseBody.data.roleList.map((role) => {
              return {
                key: role.id,
                title: role.name,
                value: role.id,
              };
            });
            setRoleList(list);
          } else {
            snackbar(
              t(PAGE_LOCALE_KEY + "fetchFail") + ": " + responseBody.message,
              "error",
            );
          }
          setRoleListLoading(false);
        }
      }
    }
  };

  // ===================== Detail field function start ===================== //

  const textInputOnChange = (e, field, callback) => {
    setError({ ...error, [field]: false });
    let { value } = e.target;
    if (callback) {
      value = callback(value);
    }
    setAgentDetailFieldInput({ ...agentDetailFieldInput, [field]: value });
  };

  const textInputOnFocus = (field) => {
    setFocus({ [field]: true });
  };

  const textInputOnBlur = (field, callback) => {
    if (callback) {
      if (agentDetailFieldInput[field]) {
        const isValidValue = callback(agentDetailFieldInput[field]);
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      } else {
        const isValidValue = callback("");
        if (!isValidValue) {
          setError({ ...error, [field]: error });
        }
      }
    }
    setFocus({ [field]: false });
  };

  const isNotEmpty = (txt) => {
    if (isNullish(txt)) return false;
    if (txt == "") return false;
    return true;
  };

  // ===================== detail field function end ===================== //

  // ===================== detail function  ===================== //

  const sendDetailUpdate = async () => {
    const requiredField = ["firstName", "lastName", "email", "roleId"];
    let hasError = false;
    requiredField.forEach((field) => {
      const fieldValue = agentDetailFieldInput[field];
      if (isEmptyOrBlank(fieldValue)) {
        setError((error) => ({ ...error, [field]: true }));
        hasError = true;
      }
    });

    if (!validateStrongPassword(agentDetailFieldInput.password)) {
      setError((error) => ({ ...error, password: true, confirm: true }));
      hasError = true;
    }
    if (agentDetailFieldInput.password != agentDetailFieldInput.confirm) {
      setError((error) => ({ ...error, confirm: true }));
      hasError = true;
    }

    const allValid = isAllInputValid(
      error.isoAgentId,
      error.firstName,
      error.lastName,
      error.password,
      error.confirm,
      error.email,
      error.role,
      error.isOTPenable,
      error.timezone,
      error.permission,
      error.roleId,
      error.status,
      hasError,
    );

    if (!allValid) {
      return;
    }

    setSubmitting(true);
    const payload = { ...agentDetailFieldInput };
    payload.permission = JSON.stringify(agentDetailFieldInput.permission);
    payload.isOTPenable = agentDetailFieldInput.isOTPenable != "0"; // cast isOTPenable to boolean
    payload.password = sha256(agentDetailFieldInput.password).toString();
    payload.timezone = agentTimezoneMapping(agentDetailFieldInput.timezone); // MAS-349. This function is aligned OBS & OP timezones behaviour when creating and rendering agent detail.

    const apiResponse = await createAgent(payload);
    if (!apiResponse || !apiResponse?.data) {
      snackbar(t("general.message.networkError"), "error");
    } else {
      switch (apiResponse.status) {
        case 401: {
          console.log("You have no authorization to view the page");
          return logout("TOKEN_EXPIRED");
        }
        case 403: {
          snackbar(t("general.message.unauthorized"), "error");
          break;
        }
        default: {
          const responseBody = apiResponse.data;
          if (responseBody.responseCode !== "0000") {
            snackbar(
              t(PAGE_LOCALE_KEY + "create.msgFailed") +
                responseCodeMapping[responseBody.responseCode ?? "error"]["en"],
              "error",
            );
          } else {
            snackbar(t(PAGE_LOCALE_KEY + "create.msgSuccess"), "success");
            const newpath = "/merchant-management/agents/agents";
            const redirectpayload = {
              name: merchantManagement
                .find((tab) => tab.tabName === "Agents")
                .page.find((page) => page.path === newpath).name,
              path: newpath,
              refresh: true,
            };

            //order of execution is important, close -> redirect
            redirectFn("close", { tabId: path });
            redirectFn("redirectPage", redirectpayload);
          }
        }
      }
    }
    setSubmitting(false);
  };

  const AgentDetailsMainContent = () => {
    return (
      <div className={s.contentContainer}>
        {submitting && <Spin type={"search"} />}
        <div className={s.content2}>
          <div className={s.itemContainer}>
            <Grid gridId={gridId}>
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.firstName.label")}
                value={agentDetailFieldInput.firstName}
                onchangeFn={(e) => textInputOnChange(e, "firstName")}
                onfocusFn={() => textInputOnFocus("firstName")}
                onblurFn={() => textInputOnBlur("firstName", isNotEmpty)}
                sxParams={{
                  textFocus: focus.firstName,
                  textError: error.firstName,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.firstName.errorInput",
                )}
              />
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.lastName.label")}
                value={agentDetailFieldInput.lastName}
                onchangeFn={(e) => textInputOnChange(e, "lastName")}
                onfocusFn={() => textInputOnFocus("lastName")}
                onblurFn={() => textInputOnBlur("lastName", isNotEmpty)}
                sxParams={{
                  textFocus: focus.lastName,
                  textError: error.lastName,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.lastName.errorInput",
                )}
              />

              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.email.label")}
                value={agentDetailFieldInput.email}
                onchangeFn={(e) => textInputOnChange(e, "email")}
                onfocusFn={() => textInputOnFocus("email")}
                onblurFn={() => textInputOnBlur("email", emailFormatValidator)}
                sxParams={{ textFocus: focus.email, textError: error.email }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.email.errorInput2",
                )}
              />
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.password.label")}
                value={agentDetailFieldInput.password}
                onchangeFn={(e) => textInputOnChange(e, "password")}
                onfocusFn={(e) => {
                  textInputOnFocus("password");
                  setAnchorEl(e.target);
                }}
                onblurFn={() => textInputOnBlur("password", isNotEmpty)}
                sxParams={{
                  textFocus: focus.password,
                  textError: error.password,
                }}
                errorMessage={
                  agentDetailFieldInput.password
                    ? t(PAGE_LOCALE_KEY + "detailColumns.password.errorInput")
                    : t(PAGE_LOCALE_KEY + "detailColumns.password.errorEmpty")
                }
              />
              <OutlinedText
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.confirm.label")}
                value={agentDetailFieldInput.confirm}
                onchangeFn={(e) => textInputOnChange(e, "confirm")}
                onfocusFn={() => textInputOnFocus("confirm")}
                onblurFn={() => textInputOnBlur("confirm", isNotEmpty)}
                sxParams={{
                  textFocus: focus.confirm,
                  textError: error.confirm,
                }}
                errorMessage={
                  agentDetailFieldInput.confirm
                    ? t(PAGE_LOCALE_KEY + "detailColumns.confirm.errorInput")
                    : t(PAGE_LOCALE_KEY + "detailColumns.confirm.errorEmpty")
                }
              />
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.isOTPenable.label")}
                selectionValue={agentDetailFieldInput.isOTPenable}
                onchangeFn={(e) => textInputOnChange(e, "isOTPenable")}
                onfocusFn={() => textInputOnFocus("isOTPenable")}
                onblurFn={() => textInputOnBlur("isOTPenable")}
                options={EnabledOptions("all")}
                sxParams={{
                  textFocus: focus.isOTPenable,
                  textError: error.isOTPenable,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.isOTPenable.errorInput",
                )}
              />
            </Grid>

            <Grid gridId={gridId}>
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.role.label")}
                selectionValue={agentDetailFieldInput.role}
                onchangeFn={(e) => textInputOnChange(e, "role")}
                onfocusFn={() => textInputOnFocus("role")}
                onblurFn={() => textInputOnBlur("role")}
                options={AgentRoleOptions("all")}
                sxParams={{ textFocus: focus.role, textError: error.role }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.role.errorInput",
                )}
              />
              <Selection
                mandatory={true}
                multiple
                label={t(PAGE_LOCALE_KEY + "detailColumns.permission.label")}
                selectionValue={agentDetailFieldInput.permission}
                onchangeFn={(e) => textInputOnChange(e, "permission")}
                onfocusFn={() => textInputOnFocus("permission")}
                onblurFn={() => textInputOnBlur("permission")}
                options={AgentPermissionOptions("all")}
                sxParams={{
                  textFocus: focus.permission,
                  textError: error.permission,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.permission.errorInput",
                )}
              />

              <Selection
                mandatory={true}
                label={t(
                  PAGE_LOCALE_KEY + "detailColumns.rolePermission.label",
                )}
                selectionValue={agentDetailFieldInput.roleId}
                onchangeFn={(e) => textInputOnChange(e, "roleId")}
                onfocusFn={() => textInputOnFocus("roleId")}
                onblurFn={() => textInputOnBlur("roleId")}
                options={roleList}
                sxParams={{
                  selectionFocus: focus.roleId,
                  selectionError: error.roleId,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.rolePermission.errorInput",
                )}
              />
            </Grid>
            <Grid gridId={gridId}>
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.timezone.label")}
                selectionValue={agentDetailFieldInput.timezone}
                onchangeFn={(e) => textInputOnChange(e, "timezone")}
                onfocusFn={() => textInputOnFocus("timezone")}
                onblurFn={() => textInputOnBlur("timezone")}
                options={AgentTimezoneOptions("all")}
                sxParams={{
                  textFocus: focus.timezone,
                  textError: error.timezone,
                }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.timezone.errorInput",
                )}
              />
              <Selection
                mandatory={true}
                label={t(PAGE_LOCALE_KEY + "detailColumns.status.label")}
                selectionValue={agentDetailFieldInput.status}
                onchangeFn={(e) => textInputOnChange(e, "status")}
                onfocusFn={() => textInputOnFocus("status")}
                onblurFn={() => textInputOnBlur("status")}
                options={AgentStatusOptions(["ACTIVE", "SUSPENDED"])}
                sxParams={{ textFocus: focus.status, textError: error.status }}
                errorMessage={t(
                  PAGE_LOCALE_KEY + "detailColumns.status.errorInput",
                )}
              />
            </Grid>
          </div>
        </div>
        <div className={s.dialogActionContainer}>
          <div></div>
          <div className={s.dialogButtonContainer}>
            <button
              className={[s.okBtn, s.functionalBtn].join(" ")}
              disabled={submitting || roleListLoading}
              onClick={() => {
                sendDetailUpdate();
              }}
            >
              {t(DIALOG_LOCALE_KEY + "btnSubmit")}
            </button>
          </div>
        </div>
        <StrongPasswordPopper
          open={focus.password}
          anchorEl={anchorEl}
          password={agentDetailFieldInput.password}
        />
      </div>
    );
  };

  // ===================== detail function end ===================== //

  const snackbar = (message, variant, durationInMs = 2000) => {
    return enqueueSnackbar(message, {
      variant,
      anchorOrigin: {
        vertical: "top",
        horizontal: "center",
      },
      autoHideDuration: durationInMs,
    });
  };

  return (
    <div className={s.container}>
      <div className={s.contentContainer}>
        {roleListLoading ? <Spin /> : <></>}
        <div
          className={[
            s.detailsContainer,
            roleListLoading ? s.loadingNow : "",
          ].join(" ")}
        >
          <div className={s.agentDetails}>
            <div className={s.header}>
              <span>{t(PAGE_LOCALE_KEY + "sectionCreateTitle")}</span>
            </div>

            <Divider />
            {AgentDetailsMainContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(AddAgent);
